import React, { useEffect, useState } from "react";
import api, { extractErrorMessage } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { useLang } from "@/i18n";
import { LANGUAGES } from "@/i18n/languages";
import { Plus, Trash2, FlaskConical, ShieldCheck, Copy, Mail, Pencil, X } from "lucide-react";
import { toast } from "sonner";

const emptyForm = {
  email: "",
  password: "",
  full_name: "",
  role: "qa_tester",
  profile_key: "player",
  test_profile_keys: [],
  test_all_profiles: false,
  country: "",
  language: "en",
  notes: "",
};

// Strong, paste-safe temporary password generator. Restricted alphabet
// (no `IilO0` and no whitespace-looking chars) so the result reliably
// survives email / chat / Excel round-trips that often add a trailing
// newline or invisible space — exactly the class of bug that triggered
// the "Invalid credentials" QA report.
const TEMP_PASSWORD_ALPHABET =
  "abcdefghjkmnpqrstuvwxyzABCDEFGHJKMNPQRSTUVWXYZ23456789!@#$%&";
const generateTempPassword = (len = 14) => {
  const cryptoObj =
    typeof window !== "undefined" && window.crypto && window.crypto.getRandomValues
      ? window.crypto
      : null;
  const buf = new Uint32Array(len);
  if (cryptoObj) cryptoObj.getRandomValues(buf);
  else for (let i = 0; i < len; i++) buf[i] = Math.floor(Math.random() * 0xffffffff);
  let out = "";
  for (let i = 0; i < len; i++) out += TEMP_PASSWORD_ALPHABET[buf[i] % TEMP_PASSWORD_ALPHABET.length];
  return out;
};

const AdminTesters = () => {
  const { user: me } = useAuth();
  const { t, lang, pickI18n, tProfile, tCountry } = useLang();
  const [testers, setTesters] = useState([]);
  const [profiles, setProfiles] = useState([]);
  const [countries, setCountries] = useState([]);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [lastInvite, setLastInvite] = useState(null);
  const [editing, setEditing] = useState(null);  // tester being edited (full row) or null
  const [editForm, setEditForm] = useState(null);
  const [editSaving, setEditSaving] = useState(false);
  // Persistent in-dialog confirmation of the password actually saved on
  // the most recent PATCH. Lives in a state so the admin can copy it
  // without having to scroll to the page-level invite card. Cleared when
  // the dialog is closed.
  const [editJustSaved, setEditJustSaved] = useState(null);
  // Manual-copy fallback shown when both the async Clipboard API and the
  // legacy execCommand path are blocked (e.g. iframe Permissions-Policy).
  // Holds the invitation text to display in a pre-selected textarea so
  // the admin can finish the copy with Cmd/Ctrl+C.
  const [manualCopyText, setManualCopyText] = useState(null);

  const isAdmin = me?.role === "admin" || me?.role === "staff";
  const isManager = me?.role === "qa_manager";

  // qa_manager scope (drives the profile checkbox availability and the
  // "Test all profiles" toggle gate).
  const managerScope = React.useMemo(() => {
    if (!isManager) return { allAllowed: true, allowedKeys: null };
    if (me?.test_all_profiles) return { allAllowed: true, allowedKeys: null };
    const keys = new Set([me?.profile_key, ...(me?.test_profile_keys || [])].filter(Boolean));
    return { allAllowed: false, allowedKeys: keys };
  }, [isManager, me]);

  const visibleProfiles = React.useMemo(() => {
    if (managerScope.allAllowed) return profiles;
    return profiles.filter((p) => managerScope.allowedKeys.has(p.key));
  }, [profiles, managerScope]);

  // If a manager flips primary profile off-scope (shouldn't happen but
  // belt-and-suspenders), reset to the first valid one.
  useEffect(() => {
    if (!isManager || managerScope.allAllowed || profiles.length === 0) return;
    if (!managerScope.allowedKeys.has(form.profile_key)) {
      const first = visibleProfiles[0];
      if (first) setForm((f) => ({ ...f, profile_key: first.key }));
    }
  }, [isManager, managerScope, profiles, visibleProfiles, form.profile_key]);

  const refresh = async () => {
    const { data } = await api.get("/admin/test-users");
    setTesters(Array.isArray(data) ? data : []);
  };

  useEffect(() => {
    refresh();
    api.get("/profiles").then((r) => setProfiles(r.data || []));
    api.get("/countries").then((r) => setCountries(r.data || []));
  }, []);

  // Country dropdown is alphabetical by the localized label, regardless of
  // ISO code. Re-sorts on language switch.
  const sortedCountries = React.useMemo(() => {
    const collator = new Intl.Collator(lang || "en", { sensitivity: "base" });
    return [...countries].sort((a, b) => {
      const an = tCountry(a.code, a);
      const bn = tCountry(b.code, b);
      return collator.compare(an, bn);
    });
  }, [countries, lang, tCountry]);

  const create = async (e) => {
    e.preventDefault();
    // Trim any clipboard-paste whitespace before validating length so the
    // saved password never carries a stray newline/space that would
    // silently break the next login as "Invalid credentials".
    const trimmedPw = (form.password || "").trim();
    if (trimmedPw.length < 8) {
      toast.error(t("phase2.passwordMinReg"));
      return;
    }
    setCreating(true);
    try {
      const { data } = await api.post("/admin/test-users", { ...form, password: trimmedPw });
      toast.success(t("phase2.qaTesterCreated"));
      setLastInvite({
        email: data?.email,
        role: data?.role || form.role,
        full_name: data?.full_name || form.full_name,
        invite_url: data?.dev_invite_url,
        temp_password: data?.dev_temp_password || trimmedPw,
        mock: !!data?.email_mock_mode,
      });
      setForm({ ...emptyForm });
      await refresh();
    } catch (err) {
      toast.error(extractErrorMessage(err));
    } finally {
      setCreating(false);
    }
  };

  const openEdit = (u) => {
    setEditing(u);
    setEditJustSaved(null);
    setEditForm({
      full_name: u.full_name || "",
      email: u.email || "",
      role: u.role || "qa_tester",
      profile_key: u.profile_key || "player",
      test_profile_keys: Array.isArray(u.test_profile_keys) ? [...u.test_profile_keys] : [],
      test_all_profiles: !!u.test_all_profiles,
      country: u.country || "",
      language: u.language || "en",
      notes: u.test_user_notes || "",
      is_active: u.is_active !== false,
      new_password: "",
      force_password_change: false,
    });
  };

  const closeEdit = () => {
    setEditing(null);
    setEditForm(null);
    setEditJustSaved(null);
  };

  const saveEdit = async () => {
    if (!editing || !editForm) return;
    // Defensive trim: paste-from-password-manager / Excel often carries
    // a trailing newline or stray space which silently breaks the next
    // login as "Invalid credentials". Strip it before any validation.
    const trimmedNewPw = (editForm.new_password || "").trim();
    if (trimmedNewPw && trimmedNewPw.length < 8) {
      toast.error(t("phase2.passwordTooShort"));
      return;
    }
    // Closes a silent UX trap: admin checks "Force password change" but
    // leaves the new-password input blank → backend only flips the flag,
    // password stays the OLD one. Admin then hands the QA user a
    // password they think is new and login fails with "Invalid
    // credentials". Make it explicit.
    if (editForm.force_password_change && !trimmedNewPw) {
      toast.error(t("phase2.qaForceChangeEmptyPassword"));
      return;
    }
    // Build minimal patch — only fields that actually changed.
    const orig = editing;
    const patch = {};
    if (editForm.full_name !== orig.full_name) patch.full_name = editForm.full_name;
    if (editForm.email !== orig.email) patch.email = editForm.email;
    if (editForm.role !== orig.role) patch.role = editForm.role;
    if (editForm.profile_key !== orig.profile_key) patch.profile_key = editForm.profile_key;
    const origExtras = (orig.test_profile_keys || []).slice().sort().join(",");
    const newExtras = (editForm.test_profile_keys || []).slice().sort().join(",");
    if (origExtras !== newExtras) patch.test_profile_keys = editForm.test_profile_keys;
    if ((!!editForm.test_all_profiles) !== (!!orig.test_all_profiles)) patch.test_all_profiles = !!editForm.test_all_profiles;
    if ((editForm.country || "") !== (orig.country || "")) patch.country = editForm.country || null;
    if (editForm.language !== orig.language) patch.language = editForm.language;
    if (editForm.notes !== (orig.test_user_notes || "")) patch.notes = editForm.notes;
    if ((!!editForm.is_active) !== (orig.is_active !== false)) patch.is_active = !!editForm.is_active;
    if (trimmedNewPw) patch.new_password = trimmedNewPw;
    if (editForm.force_password_change) patch.force_password_change = true;

    if (Object.keys(patch).length === 0) {
      toast.message(t("qaTester.noChangesToSave"));
      return;
    }
    setEditSaving(true);
    try {
      const { data } = await api.patch(`/admin/test-users/${editing.id}`, patch);
      const successMsg = t("phase2.qaTesterUpdated");
      toast.success(successMsg);
      const passwordWasRotated = !!data?.dev_temp_password;
      // If a new temporary password was issued, surface the invite card so
      // the admin can copy it for the next login. Scroll it into view so
      // the admin can't miss the actual saved password (closes the
      // "saved password ≠ what admin gave to the user" UX trap).
      if (passwordWasRotated) {
        setLastInvite({
          email: data.email,
          role: data.role,
          full_name: data.full_name,
          invite_url: `${window.location.origin}/login`,
          temp_password: data.dev_temp_password,
          mock: true,
        });
        // Persist a confirmation block INSIDE the open dialog so the
        // admin doesn't have to scroll-find the page-level invite card.
        // The block carries the EXACT password the server hashed +
        // saved (server-echoed only in MOCK MODE) so admin and QA user
        // are guaranteed to be looking at the same value.
        setEditJustSaved({
          temp_password: data.dev_temp_password,
          forced: !!editForm.force_password_change,
          email: data.email,
          updated_at: data.updated_at,
        });
      }
      await refresh();
      // Keep the dialog open when a password was rotated so the admin
      // can use the inline confirmation panel; otherwise close as before.
      if (!passwordWasRotated) {
        closeEdit();
      } else {
        // Re-baseline `editing` to the latest server row so subsequent
        // edits don't re-send fields that were just persisted.
        setEditing(data);
        setEditForm((f) => f ? { ...f, new_password: "", force_password_change: false } : f);
      }
    } catch (err) {
      toast.error(extractErrorMessage(err));
    } finally {
      setEditSaving(false);
    }
  };

  const remove = async (id, email) => {
    const msg = t("qaTester.deleteConfirm").replace("{email}", email);
    if (!window.confirm(msg)) return;
    try {
      await api.delete(`/admin/test-users/${id}`);
      toast.success(t("phase2.qaTesterDeleted"));
      await refresh();
    } catch (err) {
      toast.error(extractErrorMessage(err));
    }
  };

  // Renders the invitation as a single block of text that can be pasted
  // verbatim into email / Slack / WhatsApp. Kept as a pure helper so the
  // copy button and the manual-copy fallback share the same source of truth.
  const buildInviteText = (invite) => {
    if (!invite) return "";
    const roleLabel = invite.role === "qa_manager"
      ? t("qaTester.roleQaManager")
      : invite.role === "institutional_observer"
        ? t("qaTester.roleObserver")
        : t("qaTester.roleQaTester");
    const lines = [
      t("qaTester.helloGreeting") + ", " + (invite.full_name || invite.email),
      "",
      t("qaTester.invitedAs") + ": " + roleLabel,
      t("qaTester.emailHeader") + ": " + invite.email,
      t("qaTester.loginLinkLine") + ": " + invite.invite_url,
      t("qaTester.tempPasswordLine") + ": " + invite.temp_password,
      "",
      t("qaTester.firstLoginNote"),
    ];
    return lines.join("\n");
  };

  const copyInvite = async () => {
    if (!lastInvite) return;
    const text = buildInviteText(lastInvite);
    const successMsg = t("phase2.qaInvitationCopied");
    // Path A — modern async clipboard. Requires a secure context AND
    // permission. Both are usually fine on the preview URL, but Safari +
    // some Chromium configs reject it when the document is not focused
    // or when it can't classify the gesture as a user activation.
    try {
      if (typeof navigator !== "undefined" && navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(text);
        toast.success(successMsg);
        return;
      }
    } catch {
      // fall through to the legacy path
    }
    // Path B — legacy `document.execCommand('copy')` via a hidden textarea.
    // Works in non-secure contexts and older browsers. Keeps the user
    // gesture intact because the textarea is created + selected + copied
    // synchronously inside the click handler.
    try {
      const ta = document.createElement("textarea");
      ta.value = text;
      ta.setAttribute("readonly", "");
      ta.style.position = "fixed";
      ta.style.top = "-1000px";
      ta.style.left = "-1000px";
      ta.style.opacity = "0";
      document.body.appendChild(ta);
      ta.focus();
      ta.select();
      ta.setSelectionRange(0, ta.value.length);
      const ok = document.execCommand("copy");
      document.body.removeChild(ta);
      if (ok) {
        toast.success(successMsg);
        return;
      }
    } catch {
      // fall through to the manual-copy panel
    }
    // Path C — neither API worked (likely a permissions-policy block).
    // Surface a manual-copy textarea so the admin can still ship the
    // invitation. Pre-selected so a single Cmd/Ctrl+C completes it.
    setManualCopyText(text);
    toast.message(t("phase2.qaSelectAndCopyManually"));
  };

  const heading = t("phase2.qaTesterPageHeading");
  const subtitle = t("phase2.qaTesterPageSubtitle");

  return (
    <div className="space-y-8" data-testid="admin-testers-page">
      <div>
        <div className="tsw-overline">{t("admin.title")}</div>
        <h1 className="font-display font-bold text-3xl text-slate-900" data-testid="testers-heading">
          <FlaskConical className="inline-block w-7 h-7 -mt-1 me-2 text-emerald-600" />
          {heading}
        </h1>
        <p className="text-slate-600 mt-2 max-w-2xl">{subtitle}</p>
        {isManager && (
          <p className="text-xs text-amber-700 mt-2 max-w-2xl" data-testid="testers-manager-scope-note">
            {t("phase2.qaManagerScopeNote")}
          </p>
        )}
      </div>

      {lastInvite && (
        <div className="tsw-card p-5 border-emerald-200 bg-emerald-50/40 space-y-3" data-testid="testers-invite-card">
          <div className="flex items-center gap-2 text-emerald-800 font-display font-semibold">
            <Mail className="w-5 h-5" />
            {t("phase2.qaInvitationGenerated")}
            {lastInvite.mock && (
              <span className="tsw-chip bg-amber-100 text-amber-800 ms-2 text-[10px] uppercase tracking-wider">
                {t("phase2.qaMockModeShort")}
              </span>
            )}
          </div>
          <div className="text-sm text-slate-700">
            <div><b>{t("phase2.qaInviteForLabel")}:</b> {lastInvite.full_name} ({lastInvite.email})</div>
            <div><b>{t("phase2.qaInviteRoleLabel")}:</b> {
              lastInvite.role === "qa_manager"
                ? t("phase2.qaInviteRoleManager")
                : lastInvite.role === "institutional_observer"
                  ? t("phase2.qaInviteRoleObserver")
                  : t("phase2.qaInviteRoleTester")
            }</div>
            <div><b>{t("phase2.qaInviteLoginLinkLabel")}:</b> <a className="text-emerald-700 underline break-all" href={lastInvite.invite_url} target="_blank" rel="noreferrer" data-testid="testers-invite-url">{lastInvite.invite_url}</a></div>
            <div><b>{t("phase2.qaInviteTempPasswordLabel")}:</b> <code className="bg-white px-1.5 py-0.5 rounded border border-emerald-200 text-emerald-800" data-testid="testers-invite-temp-pw">{lastInvite.temp_password}</code></div>
          </div>
          <p className="text-xs text-slate-600">
            {t("phase2.qaInviteForceChangeOnFirstLogin")}
          </p>
          <div className="flex gap-2">
            <button onClick={copyInvite} className="tsw-btn-secondary" data-testid="testers-invite-copy">
              <Copy className="w-4 h-4" /> {t("phase2.qaCopyInvite")}
            </button>
            <button onClick={() => setLastInvite(null)} className="tsw-btn-ghost text-sm" data-testid="testers-invite-dismiss">
              {t("phase2.qaInviteDismiss")}
            </button>
          </div>
        </div>
      )}

      {manualCopyText && (
        <div
          className="tsw-card p-5 border-amber-200 bg-amber-50/40 space-y-3"
          data-testid="testers-manual-copy-panel"
        >
          <div className="font-display font-semibold text-amber-900 text-sm">
            {t("phase2.qaManualCopyHeading")}
          </div>
          <p className="text-xs text-amber-800">
            {t("phase2.qaManualCopyDetail")}
          </p>
          <textarea
            ref={(el) => {
              if (el) {
                el.focus();
                el.select();
              }
            }}
            readOnly
            value={manualCopyText}
            className="w-full h-40 tsw-input font-mono text-xs resize-none"
            data-testid="testers-manual-copy-textarea"
          />
          <div className="flex gap-2">
            <button
              type="button"
              onClick={() => setManualCopyText(null)}
              className="tsw-btn-ghost text-sm"
              data-testid="testers-manual-copy-close"
            >
              {t("phase2.qaCloseManualCopy")}
            </button>
          </div>
        </div>
      )}

      <form onSubmit={create} className="tsw-card p-5 space-y-4" data-testid="testers-create-form">
        <div className="grid sm:grid-cols-2 gap-3">
          <div className="sm:col-span-2">
            <label className="tsw-label">{t("auth.email")}</label>
            <input
              required
              type="email"
              maxLength={254}
              autoComplete="off"
              spellCheck={false}
              className="tsw-input w-full font-mono text-sm"
              value={form.email}
              onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
              data-testid="testers-email"
            />
          </div>
          <div>
            <label className="tsw-label">{t("qaTester.fullName")}</label>
            <input
              required
              maxLength={200}
              className="tsw-input"
              value={form.full_name}
              onChange={(e) => setForm((f) => ({ ...f, full_name: e.target.value }))}
              data-testid="testers-name"
            />
          </div>
          <div>
            <label className="tsw-label">{t("auth.password")}</label>
            <input
              required
              minLength={8}
              maxLength={128}
              type="text"
              className="tsw-input font-mono text-sm"
              value={form.password}
              onChange={(e) => setForm((f) => ({ ...f, password: e.target.value }))}
              data-testid="testers-password"
            />
          </div>
          <div>
            <label className="tsw-label">{t("qaTester.role")}</label>
            <select
              className="tsw-input"
              value={form.role}
              onChange={(e) => setForm((f) => ({ ...f, role: e.target.value }))}
              data-testid="testers-role"
              disabled={!isAdmin}
            >
              <option value="qa_tester">{t("qaTester.roleQaTester")}</option>
              {isAdmin && <option value="qa_manager">{t("qaTester.roleQaManager")}</option>}
              {isAdmin && (
                <option value="institutional_observer">{t("qaTester.roleObserver")}</option>
              )}
            </select>
          </div>
          <div>
            <label className="tsw-label">
              {t("qaTester.primaryProfile")}
            </label>
            <select
              required
              className="tsw-input"
              value={form.profile_key}
              onChange={(e) => setForm((f) => ({ ...f, profile_key: e.target.value }))}
              data-testid="testers-profile"
            >
              {visibleProfiles.map((p) => (
                <option key={p.key} value={p.key}>{tProfile(p.key, p)}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="tsw-label">{t("qaTester.country")}</label>
            <select
              className="tsw-input"
              value={form.country}
              onChange={(e) => setForm((f) => ({ ...f, country: e.target.value }))}
              data-testid="testers-country"
            >
              <option value="">—</option>
              {sortedCountries.map((c) => (
                <option key={c.code} value={c.code}>{`${tCountry(c.code, c)} (${c.code})`}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="tsw-label">{t("qaTester.language")}</label>
            <select
              className="tsw-input"
              value={form.language}
              onChange={(e) => setForm((f) => ({ ...f, language: e.target.value }))}
              data-testid="testers-language"
            >
              {LANGUAGES.filter((Lg) => Lg.isActive).map((Lg) => (<option key={Lg.code} value={Lg.code}>{`${Lg.code.toUpperCase()} — ${Lg.nameNative}`}</option>))}
            </select>
          </div>
        </div>

        <div className="rounded-lg border border-emerald-100 bg-emerald-50/40 p-4 space-y-3" data-testid="testers-profile-assignment">
          <div className="flex items-start justify-between gap-3 flex-wrap">
            <div>
              <div className="font-display font-semibold text-slate-900 text-sm">
                {t("qaTester.profilesQaCanAccess")}
              </div>
              <p className="text-xs text-slate-500 mt-0.5">
                {t("qaTester.profilesQaCanAccessNote")}
              </p>
            </div>
            <label
              className={`inline-flex items-center gap-2 text-sm font-medium text-emerald-800 cursor-pointer select-none ${isManager && !managerScope.allAllowed ? "opacity-40 cursor-not-allowed" : ""}`}
              data-testid="testers-all-profiles-wrap"
            >
              <input
                type="checkbox"
                checked={form.test_all_profiles}
                disabled={isManager && !managerScope.allAllowed}
                onChange={(e) => setForm((f) => ({ ...f, test_all_profiles: e.target.checked }))}
                data-testid="testers-all-profiles"
              />
              {t("qaTester.testAllProfiles")}
            </label>
          </div>
          <div className={`grid sm:grid-cols-2 lg:grid-cols-3 gap-2 ${form.test_all_profiles ? "opacity-50 pointer-events-none" : ""}`}>
            {visibleProfiles.map((p) => {
              const checked = form.test_profile_keys.includes(p.key);
              const isPrimary = p.key === form.profile_key;
              return (
                <label
                  key={p.key}
                  className={`flex items-center gap-2 rounded-md border px-3 py-2 text-sm cursor-pointer transition ${
                    isPrimary
                      ? "border-emerald-300 bg-emerald-50 text-emerald-800"
                      : checked
                        ? "border-emerald-300 bg-white text-slate-900"
                        : "border-slate-200 bg-white text-slate-600 hover:border-emerald-200"
                  }`}
                  data-testid={`testers-profile-chk-${p.key}`}
                >
                  <input
                    type="checkbox"
                    checked={isPrimary || checked}
                    disabled={isPrimary}
                    onChange={(e) =>
                      setForm((f) => ({
                        ...f,
                        test_profile_keys: e.target.checked
                          ? [...f.test_profile_keys, p.key]
                          : f.test_profile_keys.filter((k) => k !== p.key),
                      }))
                    }
                  />
                  <span className="truncate">
                    {tProfile(p.key, p)}
                    {isPrimary && (
                      <span className="ms-1 text-[10px] uppercase tracking-wide">
                        {t("qaTester.primary")}
                      </span>
                    )}
                  </span>
                </label>
              );
            })}
          </div>
        </div>

        <div>
          <label className="tsw-label">
            {t("qaTester.internalNotes")}
          </label>
          <input
            maxLength={500}
            className="tsw-input"
            value={form.notes}
            onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))}
            placeholder={t("qaTester.notesPlaceholder")}
            data-testid="testers-notes"
          />
        </div>

        <div className="flex items-center justify-between gap-3 pt-2 border-t border-slate-100">
          <p className="text-xs text-slate-500 max-w-lg">
            <ShieldCheck className="inline-block w-3.5 h-3.5 -mt-0.5 me-1 text-emerald-600" />
            {t("qaTester.testersStaffNote")}
          </p>
          <button type="submit" className="tsw-btn-primary" disabled={creating} data-testid="testers-create-submit">
            <Plus className="w-4 h-4" />
            {creating ? t("common.loading") : t("qaTester.createTester")}
          </button>
        </div>
      </form>

      <div className="tsw-card overflow-hidden">
        <div className="px-5 py-3 border-b border-slate-100 flex items-center justify-between">
          <div className="font-display font-semibold text-slate-900">
            {t("qaTester.activeTesters")}
          </div>
          <div className="text-xs text-slate-500" data-testid="testers-count">
            {testers.length}
          </div>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="testers-table">
            <thead className="bg-slate-50">
              <tr className="text-start text-xs text-slate-500 uppercase tracking-wider">
                <th className="px-4 py-3">{t("qaTester.emailHeader")}</th>
                <th className="px-4 py-3">{t("qaTester.nameHeader")}</th>
                <th className="px-4 py-3">{t("qaTester.roleHeader")}</th>
                <th className="px-4 py-3">{t("qaTester.profilesHeader")}</th>
                <th className="px-4 py-3">{t("qaTester.countryHeader")}</th>
                <th className="px-4 py-3">{t("qaTester.languageHeader")}</th>
                <th className="px-4 py-3">{t("qaTester.notesHeader")}</th>
                <th className="px-4 py-3">{t("qaTester.createdHeader")}</th>
                <th className="px-4 py-3 text-end">{t("qaTester.actionsHeader")}</th>
              </tr>
            </thead>
            <tbody>
              {testers.map((u) => (
                <tr key={u.id} className="border-t border-slate-100 hover:bg-slate-50/60" data-testid={`tester-row-${u.id}`}>
                  <td className="px-4 py-3 font-medium text-slate-900">{u.email}</td>
                  <td className="px-4 py-3 text-slate-700">{u.full_name}</td>
                  <td className="px-4 py-3">
                    <span className={`tsw-chip ${
                      u.role === "qa_manager"
                        ? "bg-amber-100 text-amber-800 border border-amber-200"
                        : u.role === "institutional_observer"
                          ? "bg-sky-100 text-sky-800 border border-sky-200"
                          : "bg-slate-100 text-slate-700"
                    }`}>
                      {u.role === "qa_manager"
                        ? t("qaTester.roleQaManager")
                        : u.role === "institutional_observer"
                          ? t("qaTester.roleObserver")
                          : t("qaTester.roleQaTester")}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-slate-700">
                    <div className="flex flex-wrap gap-1 max-w-[260px]">
                      {u.test_all_profiles ? (
                        <span className="tsw-chip bg-emerald-100 text-emerald-800 font-semibold" data-testid={`tester-profiles-all-${u.id}`}>
                          {t("qaTester.allProfiles")}
                        </span>
                      ) : (
                        <>
                          <span className="tsw-chip bg-emerald-50 text-emerald-800 border border-emerald-200" title={u.profile_key}>
                            {tProfile(u.profile_key)}
                          </span>
                          {(u.test_profile_keys || []).map((k) => (
                            <span key={k} className="tsw-chip bg-slate-100 text-slate-700" title={k}>
                              {tProfile(k)}
                            </span>
                          ))}
                        </>
                      )}
                    </div>
                  </td>
                  <td className="px-4 py-3 text-slate-700">
                    {u.country
                      ? tCountry(u.country, countries.find((x) => x.code === u.country))
                      : "—"}
                  </td>
                  <td className="px-4 py-3 text-slate-700 uppercase">{u.language || "—"}</td>
                  <td className="px-4 py-3 text-slate-500 text-xs max-w-[240px] truncate" title={u.test_user_notes || ""}>
                    {u.test_user_notes || "—"}
                  </td>
                  <td className="px-4 py-3 text-slate-500 text-xs">
                    {u.created_at ? new Date(u.created_at).toLocaleString() : "—"}
                  </td>
                  <td className="px-4 py-3 text-end">
                    <div className="inline-flex gap-1.5">
                      {(() => {
                        // QA Managers may NEVER act on another qa_manager
                        // row, nor on their own account. The buttons stay
                        // visible-but-disabled with a friendly tooltip so
                        // the affordance is discoverable without becoming
                        // a footgun. Backend mirrors this rule in the
                        // PATCH and DELETE endpoints (defense-in-depth).
                        const isOtherManager = u.role === "qa_manager";
                        const isSelf = me?.id === u.id;
                        const blockedForManager = isManager && (isOtherManager || isSelf);
                        const reason = blockedForManager
                          ? t("qaTester.actionNotAllowedManager")
                          : "";
                        return (
                          <>
                            <button
                              onClick={() => !blockedForManager && openEdit(u)}
                              disabled={blockedForManager}
                              aria-disabled={blockedForManager}
                              className="tsw-btn-secondary disabled:opacity-40 disabled:cursor-not-allowed"
                              title={blockedForManager ? reason : t("common.edit")}
                              data-testid={`tester-edit-${u.id}`}
                            >
                              <Pencil className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => !blockedForManager && remove(u.id, u.email)}
                              disabled={blockedForManager}
                              aria-disabled={blockedForManager}
                              className="tsw-btn-secondary text-red-700 border-red-200 hover:bg-red-50 disabled:opacity-40 disabled:cursor-not-allowed disabled:text-slate-400"
                              title={blockedForManager ? reason : t("common.delete")}
                              data-testid={`tester-delete-${u.id}`}
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </>
                        );
                      })()}
                    </div>
                  </td>
                </tr>
              ))}
              {testers.length === 0 && (
                <tr>
                  <td colSpan={9} className="px-4 py-10 text-center text-slate-400" data-testid="testers-empty">
                    {t("qaTester.noTestersYet")}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {editing && editForm && (
        <EditTesterDialog
          editing={editing}
          form={editForm}
          setForm={setEditForm}
          onClose={closeEdit}
          onSave={saveEdit}
          saving={editSaving}
          justSaved={editJustSaved}
          isAdmin={isAdmin}
          isManager={isManager}
          managerScope={managerScope}
          visibleProfiles={visibleProfiles}
          countries={sortedCountries}
          pickI18n={pickI18n}
        />
      )}
    </div>
  );
};

const EditTesterDialog = ({
  editing, form, setForm, onClose, onSave, saving, justSaved,
  isAdmin, isManager, managerScope, visibleProfiles, countries, pickI18n,
}) => {
  const { t, tProfile, tCountry } = useLang();
  // qa_manager constraints surfaced in the UI itself (kept consistent with backend).
  const cannotEdit =
    isManager && (
      editing.role === "qa_manager" ||
      (managerScope.allAllowed === false &&
        ![editing.profile_key, ...(editing.test_profile_keys || [])]
          .filter(Boolean)
          .every((k) => managerScope.allowedKeys.has(k)))
    );

  return (
    <div
      className="fixed inset-0 z-50 bg-slate-900/40 backdrop-blur-sm flex items-start justify-center p-4 overflow-y-auto"
      data-testid="tester-edit-dialog"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div className="tsw-card mt-8 w-full max-w-3xl bg-white p-5 sm:p-6 space-y-4">
        <div className="flex items-center justify-between gap-3">
          <div>
            <div className="tsw-overline">{t("qaTester.editTester")}</div>
            <h2 className="font-display font-bold text-xl text-slate-900">{editing.email}</h2>
          </div>
          <button onClick={onClose} className="tsw-btn-ghost" data-testid="tester-edit-close" aria-label="close">
            <X className="w-4 h-4" />
          </button>
        </div>

        {cannotEdit && (
          <div className="rounded-md bg-amber-50 border border-amber-200 text-amber-800 text-sm p-3">
            {t("qaTester.outsideManagerScope")}
          </div>
        )}

        <fieldset disabled={cannotEdit} className="space-y-4">
          <div className="grid sm:grid-cols-2 gap-3">
            <div>
              <label className="tsw-label">{t("qaTester.fullName")}</label>
              <input
                className="tsw-input"
                value={form.full_name}
                onChange={(e) => setForm((f) => ({ ...f, full_name: e.target.value }))}
                data-testid="tester-edit-fullname"
              />
            </div>
            <div>
              <label className="tsw-label">{t("qaTester.emailHeader")}</label>
              <input
                type="email"
                className="tsw-input font-mono text-sm"
                value={form.email}
                onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
                data-testid="tester-edit-email"
              />
            </div>
            <div>
              <label className="tsw-label">{t("qaTester.role")}</label>
              <select
                className="tsw-input"
                value={form.role}
                onChange={(e) => setForm((f) => ({ ...f, role: e.target.value }))}
                disabled={!isAdmin}
                data-testid="tester-edit-role"
              >
                <option value="qa_tester">{t("qaTester.roleQaTester")}</option>
                {isAdmin && <option value="qa_manager">{t("qaTester.roleQaManager")}</option>}
                {isAdmin && (
                  <option value="institutional_observer">
                    {t("qaTester.roleObserver")}
                  </option>
                )}
              </select>
            </div>
            <div>
              <label className="tsw-label">{t("qaTester.primaryProfile")}</label>
              <select
                className="tsw-input"
                value={form.profile_key}
                onChange={(e) => setForm((f) => ({ ...f, profile_key: e.target.value }))}
                data-testid="tester-edit-profile"
              >
                {visibleProfiles.map((p) => (
                  <option key={p.key} value={p.key}>{tProfile(p.key, p)}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="tsw-label">{t("qaTester.country")}</label>
              <select
                className="tsw-input"
                value={form.country}
                onChange={(e) => setForm((f) => ({ ...f, country: e.target.value }))}
                data-testid="tester-edit-country"
              >
                <option value="">—</option>
                {countries.map((c) => (
                  <option key={c.code} value={c.code}>{`${tCountry(c.code, c)} (${c.code})`}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="tsw-label">{t("qaTester.language")}</label>
              <select
                className="tsw-input"
                value={form.language}
                onChange={(e) => setForm((f) => ({ ...f, language: e.target.value }))}
                data-testid="tester-edit-language"
              >
                {LANGUAGES.filter((Lg) => Lg.isActive).map((Lg) => (<option key={Lg.code} value={Lg.code}>{`${Lg.code.toUpperCase()} — ${Lg.nameNative}`}</option>))}
              </select>
            </div>
          </div>

          {/* Profiles assignment block */}
          <div className="rounded-lg border border-emerald-100 bg-emerald-50/40 p-4 space-y-3">
            <div className="flex items-start justify-between gap-3 flex-wrap">
              <div className="font-display font-semibold text-slate-900 text-sm">
                {t("qaTester.assignedProfilesQa")}
              </div>
              <label className={`inline-flex items-center gap-2 text-sm font-medium text-emerald-800 cursor-pointer select-none ${isManager && !managerScope.allAllowed ? "opacity-40 cursor-not-allowed" : ""}`}>
                <input
                  type="checkbox"
                  checked={!!form.test_all_profiles}
                  disabled={isManager && !managerScope.allAllowed}
                  onChange={(e) => setForm((f) => ({ ...f, test_all_profiles: e.target.checked }))}
                  data-testid="tester-edit-all-profiles"
                />
                {t("qaTester.testAllProfiles")}
              </label>
            </div>
            <div className={`grid sm:grid-cols-2 lg:grid-cols-3 gap-2 ${form.test_all_profiles ? "opacity-50 pointer-events-none" : ""}`}>
              {visibleProfiles.map((p) => {
                const isPrimary = p.key === form.profile_key;
                const checked = form.test_profile_keys.includes(p.key);
                return (
                  <label
                    key={p.key}
                    className={`flex items-center gap-2 rounded-md border px-3 py-2 text-sm cursor-pointer transition ${isPrimary ? "border-emerald-300 bg-emerald-50 text-emerald-800" : checked ? "border-emerald-300 bg-white text-slate-900" : "border-slate-200 bg-white text-slate-600 hover:border-emerald-200"}`}
                    data-testid={`tester-edit-profile-chk-${p.key}`}
                  >
                    <input
                      type="checkbox"
                      checked={isPrimary || checked}
                      disabled={isPrimary}
                      onChange={(e) => setForm((f) => ({
                        ...f,
                        test_profile_keys: e.target.checked
                          ? [...f.test_profile_keys, p.key]
                          : f.test_profile_keys.filter((k) => k !== p.key),
                      }))}
                    />
                    <span className="truncate">
                      {tProfile(p.key, p)}
                      {isPrimary && (
                        <span className="ms-1 text-[10px] uppercase tracking-wide">
                          {t("qaTester.primary")}
                        </span>
                      )}
                    </span>
                  </label>
                );
              })}
            </div>
          </div>

          <div className="grid sm:grid-cols-2 gap-3">
            <div>
              <label className="tsw-label">{t("qaTester.notes")}</label>
              <input
                maxLength={500}
                className="tsw-input"
                value={form.notes}
                onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))}
                data-testid="tester-edit-notes"
              />
            </div>
            <div className="flex items-end">
              <label className="inline-flex items-center gap-2 text-sm cursor-pointer select-none px-3 py-2.5 rounded-md border border-slate-200 bg-white">
                <input
                  type="checkbox"
                  checked={!!form.is_active}
                  onChange={(e) => setForm((f) => ({ ...f, is_active: e.target.checked }))}
                  data-testid="tester-edit-active"
                />
                <span className="font-medium text-slate-800">{t("qaTester.active")}</span>
                <span className="text-xs text-slate-500">
                  ({t("qaTester.disabledNoLogin")})
                </span>
              </label>
            </div>
          </div>

          {/* Password block — never shows current value */}
          <div className="rounded-lg border border-slate-200 p-4 space-y-3" data-testid="tester-edit-password-block">
            <div className="flex items-center justify-between gap-2 flex-wrap">
              <div className="font-display font-semibold text-slate-900 text-sm">
                {t("qaTester.password")}
              </div>
              <button
                type="button"
                onClick={() => {
                  const pw = generateTempPassword(14);
                  setForm((f) => ({ ...f, new_password: pw, force_password_change: true }));
                }}
                className="tsw-btn-ghost text-xs text-emerald-700 hover:bg-emerald-50"
                data-testid="tester-edit-generate-temp-pw"
              >
                {t("qaTester.generateStrongTempPwd")}
              </button>
            </div>
            <p className="text-xs text-slate-500">
              {t("qaTester.pwdInstruction")}
            </p>
            <input
              type="text"
              className="tsw-input font-mono text-sm"
              value={form.new_password}
              onChange={(e) => setForm((f) => ({ ...f, new_password: e.target.value }))}
              placeholder={t("qaTester.newTempPwdPlaceholder")}
              data-testid="tester-edit-new-password"
              autoComplete="off"
              spellCheck={false}
            />
            <label className="inline-flex items-center gap-2 text-sm cursor-pointer select-none">
              <input
                type="checkbox"
                checked={!!form.force_password_change}
                onChange={(e) => setForm((f) => ({ ...f, force_password_change: e.target.checked }))}
                data-testid="tester-edit-force-pw-change"
              />
              <span>{t("qaTester.forcePasswordChange")}</span>
            </label>
          </div>

          {/* Post-save confirmation: persistent inside the dialog so the
              admin can copy the EXACT saved password without having to
              scroll-find the page-level invite card. Only visible after
              a successful PATCH that rotated the password. */}
          {justSaved && justSaved.temp_password && (
            <div
              className="rounded-lg border border-emerald-300 bg-emerald-50 p-4 space-y-2"
              data-testid="tester-edit-saved-confirmation"
            >
              <div className="font-display font-semibold text-emerald-900 text-sm">
                {t("qaTester.pwdUpdatedShare")}
              </div>
              <div className="text-xs text-emerald-900/80">
                {t("qaTester.forLabel")}: <b>{justSaved.email}</b>
                {justSaved.forced && (
                  <span className="ms-2 text-emerald-800">
                    · {t("qaTester.forcedNextLogin")}
                  </span>
                )}
              </div>
              <div className="flex items-center gap-2 flex-wrap">
                <code
                  className="bg-white px-2 py-1.5 rounded border border-emerald-200 text-emerald-900 font-mono text-sm select-all break-all"
                  data-testid="tester-edit-saved-temp-pw"
                >{justSaved.temp_password}</code>
                <button
                  type="button"
                  onClick={async () => {
                    try {
                      await navigator.clipboard.writeText(justSaved.temp_password);
                      toast.success(t("qaTester.copied"));
                    } catch {
                      toast.error(t("qaTester.copyFailed"));
                    }
                  }}
                  className="tsw-btn-secondary text-xs"
                  data-testid="tester-edit-saved-copy"
                >
                  <Copy className="w-3.5 h-3.5" />
                  {t("qaTester.copyPassword")}
                </button>
              </div>
              <p className="text-xs text-emerald-900/70">
                {t("qaTester.serverHashedNote")}
              </p>
            </div>
          )}
        </fieldset>

        <div className="flex items-center justify-end gap-2 pt-2 border-t border-slate-100">
          <button onClick={onClose} className="tsw-btn-ghost" data-testid="tester-edit-cancel">
            {t("common.cancel")}
          </button>
          <button
            onClick={onSave}
            disabled={saving || cannotEdit}
            className="tsw-btn-primary"
            data-testid="tester-edit-save"
          >
            {saving ? t("qaTester.saving") : t("qaTester.saveChanges")}
          </button>
        </div>
      </div>
    </div>
  );
};

export default AdminTesters;
